import { TCasesDocumentType } from "../../models/CaseDocumentType1";
import { TRoadblockCaseData, TRoadblockCheckListItem } from "../roadblockTypesAndCodecs";
import { TRoadblockEvidenceFact, TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";
import { fact, formatEvidenceDate } from "../evidence/evidenceHelpers";

const clientsLink: TRoadblockEvidenceLink = { block: "case_details", section: "clients" };

const docSlotLabel = (type: TCasesDocumentType): string =>
    type === "grant_of_probate" ? "Grant of probate"
    : type === "letters_of_admin" ? "Letters of administration"
    : type === "will" ? "Will"
    : type === "death_certificate" ? "Death certificate"
    : type;

const docSlotFacts = (c: TRoadblockCaseData, types: Array<TCasesDocumentType>): Array<TRoadblockEvidenceFact> =>
    c.cases_documents
        .filter(({ type }) => types.includes(type))
        .map((doc) => fact(
            docSlotLabel(doc.type),
            doc.received_date !== null ? `Received ${formatEvidenceDate(doc.received_date)}` : "Not received",
            doc.received_date !== null,
            clientsLink,
        ));

export const isProbateReason = (c: TRoadblockCaseData) =>
    c.case.seller_reason_for_sale === "probate"
    || c.case.seller_reason_for_sale === "probate_as1"
    || c.case.seller_reason_for_sale === "probate_djp"
    || c.case.seller_reason_for_sale === "probate_concessionary";

export const allDocumentSlotsReceived = (c: TRoadblockCaseData, types: Array<TCasesDocumentType>) => {
    const slots = c.cases_documents.filter(({ type }) => types.includes(type));
    return slots.length > 0 && slots.every(({ received_date }) => received_date !== null);
};

export const anyDocSlotExists = (c: TRoadblockCaseData, types: Array<TCasesDocumentType>) =>
    c.cases_documents.filter(({ type }) => types.includes(type)).length > 0;

export const probateChecklistItems = (): Array<TRoadblockCheckListItem> => [
    {
        type: "grant_of_probate_received",
        is_resolved_by_short_description: "Grant of probate or letters of administration received",
        create: {
            create_condition_description: "Cases where the seller's reason for sale is a probate variant",
            predicate: (c) => isProbateReason(c),
        },
        resolve: {
            is_resolved_by_long_description: `Every grant of probate and letters of administration document on the case being uploaded and marked as received. Stays open while any expected document slot is still unreceived.`,
            predicate: (c) => allDocumentSlotsReceived(c, ["grant_of_probate", "letters_of_admin"]),
            evidence: (c) => docSlotFacts(c, ["grant_of_probate", "letters_of_admin"]),
        },
    },
    {
        type: "grant_applied_for",
        is_resolved_by_short_description: "Grant of probate or letters of administration applied for",
        create: {
            create_condition_description: "Probate cases",
            predicate: (c) => isProbateReason(c),
        },
        resolve: {
            is_resolved_by_long_description: `Recording the grant application date on the case, OR the grant of probate / letters of administration already being received (no need to backfill the application date once the grant is in).`,
            predicate: (c) =>
                c.case.grant_application_date !== null
                || allDocumentSlotsReceived(c, ["grant_of_probate", "letters_of_admin"]),
            evidence: (c) => {
                const grantsReceived = allDocumentSlotsReceived(c, ["grant_of_probate", "letters_of_admin"]);
                return [
                    fact(
                        "Grant applied for",
                        c.case.grant_application_date !== null
                            ? `Applied ${formatEvidenceDate(c.case.grant_application_date)}`
                            : grantsReceived ? "Grant already received" : "Not applied",
                        c.case.grant_application_date !== null || grantsReceived,
                    ),
                ];
            },
        },
    },
    {
        type: "probate_grant_matched_on_hmcts",
        is_resolved_by_short_description: "Matched grant of probate available to obtain from HMCTS",
        create: {
            create_condition_description: "Probate cases where the automated HMCTS grant check has a confirmed match against the deceased",
            predicate: (c) => isProbateReason(c) && c.probate_grant_matches.some((m) => m.status === "confirmed"),
        },
        resolve: {
            is_resolved_by_long_description: `The matched grant of probate / letters of administration being uploaded and marked as received on the case. The automated check has found the grant is available on HMCTS — obtain it and mark it received.`,
            predicate: (c) => allDocumentSlotsReceived(c, ["grant_of_probate", "letters_of_admin"]),
            evidence: (c) => {
                const received = allDocumentSlotsReceived(c, ["grant_of_probate", "letters_of_admin"]);
                return c.probate_grant_matches
                    .filter((m) => m.status === "confirmed")
                    .map((m) => fact(
                        `Grant matched${m.matched_name !== null ? ` for ${m.matched_name}` : ""}`,
                        [
                            m.match_confidence === "strong" ? "Strong match" : "Staff-confirmed match",
                            `probate #${m.probate_number}`,
                            m.matched_on !== null ? `found ${formatEvidenceDate(m.matched_on)}` : null,
                        ].filter((part): part is string => part !== null).join(" · "),
                        received,
                        clientsLink,
                    ));
            },
        },
    },
    {
        type: "will_received",
        is_resolved_by_short_description: "Will received",
        create: {
            create_condition_description: "Probate cases where a will document slot has been created on the case",
            predicate: (c) => isProbateReason(c) && anyDocSlotExists(c, ["will"]),
        },
        resolve: {
            is_resolved_by_long_description: `Every will document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
            predicate: (c) => allDocumentSlotsReceived(c, ["will"]),
            evidence: (c) => docSlotFacts(c, ["will"]),
        },
    },
    {
        type: "death_certificate_received",
        is_resolved_by_short_description: "Death certificate received",
        create: {
            create_condition_description: "Probate cases where a death certificate document slot has been created on the case",
            predicate: (c) => isProbateReason(c) && anyDocSlotExists(c, ["death_certificate"]),
        },
        resolve: {
            is_resolved_by_long_description: `Every death certificate document slot on the case being uploaded and marked as received. Stays open while any slot is still unreceived.`,
            predicate: (c) => allDocumentSlotsReceived(c, ["death_certificate"]),
            evidence: (c) => docSlotFacts(c, ["death_certificate"]),
        },
    },
];
